import React from 'react';
import {
  Avatar as MuiAvatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import styled from 'styled-components';
import { spacing } from '@mui/system';

const Centered = styled.div`
  text-align: center;
`;

const Avatar = styled(MuiAvatar)`
  display: inline-block;
  height: 128px;
  width: 128px;
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const Spacer = styled.div<{ mb?: number }>`
  ${spacing}
`;

export interface ProfileDetailsCardProps extends LocalizeContextProps {
  /** User's first name */
  name: string;
  
  /** User's surname(s) */
  surnames: string;
  
  /** User's UUID */
  uuid: string;
  
  /** User type: researcher or hospital staff */
  userType: 'researcher' | 'hospital';
  
  /** Optional avatar image URL */
  avatarUrl?: string | null;
  
  /** Role in current investigation (e.g. DOCTOR, NURSE). Omit when no investigation or no role. */
  role?: { name: string } | null;
}

/**
 * ProfileDetailsCard Component
 * 
 * Displays user profile information in a card:
 * - Avatar (placeholder for now)
 * - Full name
 * - UUID
 * - User type badge
 * - Edit profile button (disabled - coming soon)
 */
const ProfileDetailsCardComponent: React.FC<ProfileDetailsCardProps> = ({
  name,
  surnames,
  uuid,
  userType,
  avatarUrl,
  role,
  translate,
}) => {
  const fullName = `${name} ${surnames}`;
  const userTypeLabel = userType === 'researcher' 
    ? translate('profile.user_type.researcher')
    : translate('profile.user_type.hospital');
  const roleLabel = role
    ? (translate('investigation.share.roles.' + role.name) !== 'Missing translationId: investigation.share.roles.' + role.name
        ? translate('investigation.share.roles.' + role.name)
        : role.name)
    : translate('profile.role.none');

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <Translate id="profile.details" />
        </Typography>

        <Spacer mb={4} />

        <Centered>
          {avatarUrl ? (
            <Avatar alt={fullName} src={avatarUrl} />
          ) : (
            <Avatar>
              {name.charAt(0).toUpperCase()}
              {surnames.charAt(0).toUpperCase()}
            </Avatar>
          )}

          <Typography variant="body1" component="div" gutterBottom>
            <Box fontWeight="fontWeightMedium" fontSize="1.1rem">
              {fullName}
            </Box>
          </Typography>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            UUID: {uuid}
          </Typography>

          <Box sx={{ mt: 1, mb: 2 }}>
            <Chip 
              label={userTypeLabel}
              color={userType === 'researcher' ? 'primary' : 'secondary'}
              size="small"
              sx={{ mr: 0.5 }}
            />
            <Chip
              label={roleLabel}
              variant="outlined"
              size="small"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Tooltip title={translate('profile.coming_soon') as string}>
            <span>
              <Button 
                variant="outlined" 
                size="small"
                disabled
                sx={{ mt: 1 }}
              >
                <Translate id="profile.edit_profile" /> 🔒
              </Button>
            </span>
          </Tooltip>
        </Centered>
      </CardContent>
    </Card>
  );
};

export default withLocalize(ProfileDetailsCardComponent);

